import { Alert, Anchor, Badge, Group, Table, Text, TextInput, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { listContent } from "@/api/authoring";
import { useTenantScope } from "@/auth/tenant";
import { AppLayout } from "@/components/AppLayout";
import { TenantField } from "@/components/TenantField";
import { TableSkeleton } from "@/components/TableSkeleton";
import { t } from "@/i18n";

const _STAGE_COLOR: Record<string, string> = {
  live: "green",
  draft: "gray",
  review: "yellow",
  retired: "red",
};

/**
 * Content browser (phase2-admin-ui slice) — one row per insight in the base
 * layer (+ the org's net-new/override files when an org is given), linking to
 * the content detail/editor page.
 */
export function ContentListPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const { org, pinned } = useTenantScope(params.get("org") ?? "");
  const [filter, setFilter] = useState("");

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["authoring-content", org],
    queryFn: () => listContent(org || undefined),
  });

  const needle = filter.trim().toLowerCase();
  const rows = (data?.insights ?? []).filter(
    (row) => needle === "" || row.insight_id.toLowerCase().includes(needle),
  );

  return (
    <AppLayout breadcrumbs={[{ label: t("authoring.content_title") }]}>
      <Title order={1}>{t("authoring.content_title")}</Title>
      <Group mt="md" mb="md" align="flex-end">
        <TenantField
          org={org}
          pinned={pinned}
          onChange={(next) => setParams(next ? { org: next } : {})}
        />
        <TextInput
          label={t("authoring.search")}
          placeholder={t("authoring.content_search_placeholder")}
          value={filter}
          onChange={(e) => setFilter(e.currentTarget.value)}
          style={{ flexGrow: 1 }}
        />
      </Group>
      {isPending ? (
        <TableSkeleton label={t("authoring.content_loading")} />
      ) : isError ? (
        <Alert color="red" title={t("authoring.error_title")}>
          {error instanceof Error ? error.message : t("authoring.error_generic")}
        </Alert>
      ) : rows.length === 0 ? (
        <Text c="dimmed">{t("authoring.content_empty")}</Text>
      ) : (
        <>
          <Text size="sm" c="dimmed" mb="xs">
            {rows.length} / {data.total} {t("authoring.content_count_suffix")}
          </Text>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("authoring.col_insight")}</Table.Th>
                <Table.Th>{t("authoring.col_regions")}</Table.Th>
                <Table.Th>{t("authoring.col_languages")}</Table.Th>
                <Table.Th>{t("authoring.col_lifecycle")}</Table.Th>
                <Table.Th>{t("authoring.col_severity")}</Table.Th>
                <Table.Th>{t("authoring.col_citations")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.insight_id}>
                  <Table.Td>
                    <Anchor
                      component={Link}
                      to={`/authoring/content/${encodeURIComponent(
                        row.insight_id.replace(/^#/, ""),
                      )}${org ? `?org=${encodeURIComponent(org)}` : ""}`}
                    >
                      {row.insight_id}
                    </Anchor>{" "}
                    {row.org_override && (
                      <Badge size="xs" color="grape" variant="light">
                        {t("authoring.badge_override")}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>{row.regions.join(", ")}</Table.Td>
                  <Table.Td>{row.languages.join(", ")}</Table.Td>
                  <Table.Td>
                    <Badge
                      color={_STAGE_COLOR[row.lifecycle_stage.toLowerCase()] ?? "blue"}
                      variant="light"
                    >
                      {row.lifecycle_stage}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{row.severity ?? "—"}</Table.Td>
                  <Table.Td>{row.citation_count}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </>
      )}
    </AppLayout>
  );
}
